<template>
    <router-link
    :to="orderRoute"
    class="order-widget-compact d-block text-decoration-none noselect"
    :class="status.cardClass"
    :aria-label="$t('delivery.open_order_details')"
    :title="$t('delivery.open_order_details')"
    @click="preventInvalidNavigation"
    >
        <div class="order-widget-compact__layout">
            <div class="order-widget-compact__body min-w-0">
                <p class="order-widget-compact__headline fw-bolder text-gray-900 mb-0">
                    <BootstrapIcon v-if="customerName" class="flex-shrink-0" name="person" :size="15" />
                    <span>{{ headline }}</span>
                </p>

                <div v-if="metadata.length > 0" class="order-widget-compact__meta mt-1">
                    <span v-for="detail in metadata" :key="detail.key" class="order-widget-compact__meta-item">
                        <BootstrapIcon :name="detail.icon" :size="14" />
                        <span v-if="detail.label" class="visually-hidden">{{ detail.label }}:</span>
                        <span>{{ detail.value }}</span>
                    </span>
                </div>
            </div>

            <div class="order-widget-compact__trailing">
                <span class="order-widget-compact__status" :class="status.pillClass">
                    <BootstrapIcon :color="status.iconColor" name="circle-fill" :size="8" />
                    <span>{{ $t(status.labelKey) }}</span>
                </span>
                <BootstrapIcon class="order-widget-compact__chevron" name="chevron-right" :size="16" />
            </div>
        </div>
    </router-link>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'

const STATUS = Object.freeze({
    order_pending: {
        labelKey: 'delivery.pending',
        cardClass: 'order-widget-compact--pending',
        pillClass: 'order-widget-compact__status--pending',
        iconColor: 'fill-coral-500 text-coral-500'
    },
    order_preparing: {
        labelKey: 'delivery.preparing',
        cardClass: 'order-widget-compact--preparing',
        pillClass: 'order-widget-compact__status--preparing',
        iconColor: 'fill-teal-500 text-teal-500'
    },
    order_completed: {
        labelKey: 'delivery.completed',
        cardClass: 'order-widget-compact--completed',
        pillClass: 'order-widget-compact__status--completed',
        iconColor: 'fill-cyan-500 text-cyan-500'
    },
    order_discarded: {
        labelKey: 'delivery.discarded',
        cardClass: 'order-widget-compact--discarded',
        pillClass: 'order-widget-compact__status--discarded',
        iconColor: 'fill-orange-300 text-orange-300'
    },
    order_delivered: {
        labelKey: 'delivery.handed',
        cardClass: 'order-widget-compact--delivered',
        pillClass: 'order-widget-compact__status--delivered',
        iconColor: 'fill-brown-500 text-brown-500'
    }
})

const UNKNOWN_STATUS = Object.freeze({
    labelKey: 'order_widget.unknown_status',
    cardClass: 'order-widget-compact--unknown',
    pillClass: 'order-widget-compact__status--unknown',
    iconColor: 'fill-gray-500 text-gray-500'
})

export default {
    name: 'OrderWidgetCompact',
    components: {
        BootstrapIcon
    },
    props: {
        order: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        safeOrder: function () {
            return this.order && typeof this.order === 'object' && !Array.isArray(this.order) ? this.order : {}
        },
        safeItems: function () {
            return Array.isArray(this.safeOrder.items)
                ? this.safeOrder.items.filter(item => item && typeof item === 'object' && !Array.isArray(item))
                : []
        },
        safeCustomer: function () {
            return this.safeOrder.customer && typeof this.safeOrder.customer === 'object' && !Array.isArray(this.safeOrder.customer)
                ? this.safeOrder.customer
                : {}
        },
        customerName: function () {
            return this.hasText(this.safeCustomer.name) ? this.safeCustomer.name.trim() : ''
        },
        processingMemberName: function () {
            const member = this.safeOrder.delivery
            return this.safeOrder.status === 'order_preparing' &&
                member && typeof member === 'object' && !Array.isArray(member) && this.hasText(member.username)
                ? member.username.trim()
                : ''
        },
        productCountText: function () {
            const key = this.safeItems.length === 1 ? 'order_widget.product_count_one' : 'order_widget.product_count_many'
            return this.$t(key, { COUNT: this.safeItems.length })
        },
        headline: function () {
            return this.customerName || this.productCountText
        },
        createdAgo: function () {
            return this.formatTimeAgo(this.safeOrder.created_at)
        },
        metadata: function () {
            const details = []
            if (this.customerName) {
                details.push({ key: 'products', icon: 'stack', label: this.$t('order_widget.products'), value: this.productCountText })
            }
            if (this.processingMemberName) {
                details.push({ key: 'processing-member', icon: 'cart', label: this.$t('delivery.processed_by'), value: this.processingMemberName })
            }
            if (this.createdAgo) {
                details.push({ key: 'created', icon: 'clock', label: this.$t('order_widget.added'), value: this.createdAgo })
            }
            return details
        },
        status: function () {
            return this.hasText(this.safeOrder.status) && STATUS[this.safeOrder.status]
                ? STATUS[this.safeOrder.status]
                : UNKNOWN_STATUS
        },
        orderRoute: function () {
            return this.hasText(this.safeOrder.id)
                ? `/delivery/order?id=${encodeURIComponent(this.safeOrder.id.trim())}`
                : '/delivery'
        },
        canNavigate: function () {
            return this.hasText(this.safeOrder.id)
        }
    },
    methods: {
        hasText: function (value) {
            return typeof value === 'string' && value.trim().length > 0
        },
        formatTimeAgo: function (timestamp) {
            if (!timestamp) return ''
            const date = new Date(timestamp)
            if (Number.isNaN(date.getTime())) return ''

            const elapsedSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
            const intervals = [
                [31536000, 'y'],
                [2592000, 'M'],
                [86400, 'd'],
                [3600, 'h'],
                [60, 'm']
            ]
            for (const [seconds, suffix] of intervals) {
                if (elapsedSeconds >= seconds) return `${Math.floor(elapsedSeconds / seconds)}${suffix}`
            }
            return `${elapsedSeconds}s`
        },
        preventInvalidNavigation: function (event) {
            if (!this.canNavigate) event.preventDefault()
        }
    }
}
</script>
